import { useCallback, useRef, useState } from "react";
import { onboardingSaveField } from "@/features/portal/api";
import { ApiError } from "@/lib/api";
import { maskedDateToISO, onlyDigits } from "@/lib/masks";

/** Field names whose masked value is sent to the server as raw digits. */
const DIGIT_FIELDS = new Set([
  "ssn",
  "spouse_ssn",
  "ein",
]);

/** Field names whose `MM/DD/YYYY` value is sent as ISO `YYYY-MM-DD`. */
const DATE_FIELDS = new Set([
  "date_of_birth",
  "date_of_death",
  "date_of_marriage",
  "spouse_date_of_birth",
  "spouse_date_of_death",
]);

/**
 * Normalize a single field's UI value to what the autosave endpoint expects:
 * SSN/EIN → digits (server re-formats/validates), masked dates → ISO. Sensitive
 * values (SSN/EIN) are transformed here but never logged.
 */
export function normalizeFieldValue(name: string, value: unknown): unknown {
  if (typeof value === "string") {
    if (DIGIT_FIELDS.has(name)) return onlyDigits(value);
    if (DATE_FIELDS.has(name)) {
      const iso = maskedDateToISO(value);
      // Blank or incomplete dates save as empty so the server can clear them.
      return value.trim() === "" ? "" : iso;
    }
  }
  return value;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Blur-save autosave for the onboarding wizard. Each field commits independently
 * via PATCH `/portal/onboarding/field`. Tracks a coarse status for the subtle
 * "Saved" indicator; individual field failures surface a boolean the wizard can
 * use to nudge the user without blocking data entry.
 */
export function useOnboardingAutosave(onboardingToken: string) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  // Guards against a stale "saving" flicker overwriting a newer result.
  const seq = useRef(0);

  const saveField = useCallback(
    async (name: string, value: unknown): Promise<boolean> => {
      const payload = { [name]: normalizeFieldValue(name, value) };
      const current = ++seq.current;
      setStatus("saving");
      try {
        await onboardingSaveField(onboardingToken, payload);
        if (seq.current === current) setStatus("saved");
        return true;
      } catch (err) {
        if (seq.current === current) setStatus("error");
        // Do not log the payload — it may hold PII (SSN/EIN/DOB).
        if (err instanceof ApiError && err.status === 400) return false;
        return false;
      }
    },
    [onboardingToken],
  );

  return { status, saveField };
}
